import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    LifeBuoy, ExternalLink, Mail, ChevronDown, Search, BookOpen, FileText, CheckSquare,
    ClipboardList, ScanLine, MailCheck, ShieldCheck, Settings, CreditCard, Rocket,
} from "lucide-react";

const KB = [
    {
        id: "getting-started", icon: Rocket, title: "Getting started & signing in",
        keywords: "login sign in roles password sso veracross 2-step mfa",
        blocks: [
            "ProcureEd runs your school's purchasing from the first request to a posted AP invoice in Veracross. What you can do depends on your role: Requester, Approver, Purchasing Admin or School Admin.",
            "School & Purchasing Admins sign in at the School sign-in page with email & password and complete 2-step verification with an authenticator app. Requesters and Approvers sign in with Veracross SSO using your school route.",
            ["Open the sign-in page and choose Email & password or Veracross SSO.",
             "Enter your credentials; admins then enter the 6-digit code from their authenticator app.",
             "If you lose your authenticator, ask a School Admin (or the platform team) to reset your 2-step verification."],
        ],
    },
    {
        id: "requests", icon: FileText, title: "Creating & submitting purchase requests",
        keywords: "purchase request pr line items vendor gl account submit draft",
        blocks: [
            "A purchase request captures what you want to buy, from which vendor, and how it should be coded.",
            ["Go to Purchase Requests → New Request.",
             "Pick the vendor and add one or more line items (description, quantity, unit price and GL account).",
             "Add a department, cost centre, need-by date and justification if required.",
             "Save as a draft, then click Submit to send it into the approval workflow."],
            "Once submitted, the request is routed automatically to the right approver(s) based on your school's approval rules.",
        ],
    },
    {
        id: "approvals", icon: CheckSquare, title: "Approvals",
        keywords: "approve reject approver multi level cfo department head chain",
        blocks: [
            "Approvers see requests waiting on them under Approvals. Rules can require multiple levels — for example a Department Head first, then the CFO for amounts over a threshold.",
            ["Open Approvals and review the request and its line items.",
             "Approve to pass it to the next level, or Reject with a comment.",
             "When the final approval is given, a Purchase Order is issued automatically."],
        ],
    },
    {
        id: "orders", icon: ClipboardList, title: "Purchase orders & PDFs",
        keywords: "purchase order po pdf download bill to ship to vendor logo footer",
        blocks: [
            "A branded PO PDF is generated the moment a request is fully approved. Open Purchase Orders and use the download button to view any PO.",
            "The PDF shows your school's Bill To / Ship To details (with an ATTN line for the requester), the full vendor information, the approval trail, and your school logo and footer.",
            "School Admins can add a logo and footer under School Settings → Purchase Order.",
        ],
    },
    {
        id: "invoices", icon: ScanLine, title: "Invoices & OCR",
        keywords: "invoice upload ocr scan extract review line items gl awaiting review",
        blocks: [
            "Under Invoices & OCR you can upload a vendor invoice (PDF or image). ProcureEd's AI reads the header and every line item with a confidence score.",
            ["Click Upload Invoice and choose the file (optionally link it to a PO).",
             "Open the invoice and click Extract with AI to read the line items.",
             "Review each row, assign GL accounts, correct anything the AI got wrong, and Save.",
             "Invoices imported from email arrive already scanned with the status Awaiting Review."],
        ],
    },
    {
        id: "email-invoices", icon: MailCheck, title: "Review email invoices",
        keywords: "email inbox microsoft 365 google workspace imap mailbox import delete staging",
        blocks: [
            "Vendors often email invoices. ProcureEd can watch a dedicated mailbox and stage the PDF attachments for review.",
            ["A School Admin connects the mailbox under School Settings → Email Inbox (Microsoft 365, Google Workspace or IMAP) and follows the on-screen setup guide.",
             "New emails appear under Review Email Invoices — click Check for new emails to pull the latest.",
             "Click Import to turn an attachment into an invoice (it lands in Invoices & OCR as Awaiting Review), or Delete to discard anything that isn't an invoice."],
        ],
    },
    {
        id: "matching", icon: ShieldCheck, title: "3-way match & posting to Veracross",
        keywords: "three way match po invoice veracross post ap gl journal",
        blocks: [
            "Before posting, ProcureEd checks the invoice against its purchase order (a 3-way match) so only approved, fully-coded, documented invoices reach your ledger.",
            ["Open an invoice and link it to its purchase order if it isn't matched automatically.",
             "Run the 3-Way Match and resolve any exceptions (totals, line counts, missing GL codes).",
             "When it passes, click Post to Veracross to create the AP invoice, line items and attach the source file."],
        ],
    },
    {
        id: "settings", icon: Settings, title: "School settings",
        keywords: "settings profile veracross sync users roles logo footer email inbox security recovery codes",
        blocks: [
            "School Admins manage their school under School Settings:",
            ["Profile — your school's address and contact details (used on the PO).",
             "Veracross — connect your Veracross route and credentials, and sync vendors, GL accounts and staff.",
             "Purchase Order — upload your logo and set the PO footer.",
             "Email Inbox — connect the mailbox that receives vendor invoices.",
             "Users & Roles — assign roles and set each requester's initial approver.",
             "Security — regenerate your one-time recovery codes."],
        ],
    },
    {
        id: "billing", icon: CreditCard, title: "Billing & subscription",
        keywords: "billing subscription plan trial stripe invoice receipt renew",
        blocks: [
            "School Admins can review the plan, trial and renewal details and download receipts under Billing.",
            "Schools start on a 30-day free trial; after that a subscription keeps the workspace active. If a subscription lapses, some actions are paused until it is renewed.",
        ],
    },
];

function Article({ item, open, onToggle }) {
    const Icon = item.icon;
    return (
        <div className="bg-white border border-border rounded-xl overflow-hidden" data-testid={`kb-article-${item.id}`}>
            <button onClick={onToggle} className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-secondary/40 transition-colors" data-testid={`kb-toggle-${item.id}`}>
                <span className="grid place-items-center h-9 w-9 rounded-lg bg-emerald-50 text-brand-teal shrink-0"><Icon className="h-4 w-4" /></span>
                <span className="font-semibold text-brand-emerald flex-1">{item.title}</span>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="px-5 pb-5 pt-1 space-y-3 text-sm text-slate-600 leading-relaxed border-t border-border">
                    {item.blocks.map((b, i) => Array.isArray(b) ? (
                        <ol key={i} className="list-decimal pl-5 space-y-1.5">{b.map((s, j) => <li key={j}>{s}</li>)}</ol>
                    ) : (
                        <p key={i}>{b}</p>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function Help() {
    const [cfg, setCfg] = useState({ support_url: "", support_email: "" });
    const [q, setQ] = useState("");
    const [openId, setOpenId] = useState("getting-started");

    useEffect(() => { api.get("/support/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return KB;
        return KB.filter((a) => (a.title + " " + a.keywords + " " + JSON.stringify(a.blocks)).toLowerCase().includes(s));
    }, [q]);

    return (
        <div className="space-y-6 pe-fade-up" data-testid="help-page">
            <div>
                <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-brand-emerald flex items-center gap-2">
                    <LifeBuoy className="h-6 w-6 text-brand-teal" /> Help &amp; Support
                </h1>
                <p className="text-muted-foreground text-sm mt-1">Browse the knowledge base or reach our support team.</p>
            </div>

            {/* Support request */}
            <div className="rounded-xl border border-emerald-100 bg-gradient-to-r from-emerald-50 to-white p-5 sm:p-6" data-testid="support-request-card">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-semibold text-brand-emerald">Need a hand?</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">Submit a support request and our team will get back to you.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {cfg.support_url ? (
                            <a href={cfg.support_url} target="_blank" rel="noopener noreferrer" data-testid="submit-support-link">
                                <Button className="bg-brand-emerald hover:bg-brand-emeraldhover rounded-lg">
                                    Submit a support request <ExternalLink className="h-4 w-4 ml-1.5" />
                                </Button>
                            </a>
                        ) : cfg.support_email ? null : (
                            <span className="text-sm text-muted-foreground" data-testid="support-not-configured">Support link not set up yet — contact your administrator.</span>
                        )}
                        {cfg.support_email && (
                            <a href={`mailto:${cfg.support_email}`} data-testid="support-email-link">
                                <Button variant="outline" className="rounded-lg"><Mail className="h-4 w-4 mr-1.5" /> {cfg.support_email}</Button>
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Knowledge base */}
            <div className="flex items-center gap-2 text-brand-emerald font-semibold">
                <BookOpen className="h-4 w-4 text-brand-teal" /> User manual & knowledge base
            </div>
            <div className="relative max-w-md">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the knowledge base…" className="pl-9" data-testid="kb-search" />
            </div>

            <div className="space-y-3">
                {filtered.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground bg-white border border-border rounded-xl">No articles match “{q}”.</div>
                ) : filtered.map((item) => (
                    <Article key={item.id} item={item} open={openId === item.id} onToggle={() => setOpenId(openId === item.id ? null : item.id)} />
                ))}
            </div>
        </div>
    );
}
